/**
 * Usage over time, for the Spend charts.
 *
 *   GET /v1/projects/{project}/usage/timeseries?range=month&group_by=resource[&resource=cpu][&environment=production]
 *   GET /v1/workspaces/{workspace}/usage/timeseries?range=month&group_by=project[&resource=cpu]
 *   GET /v1/admin/usage/timeseries?range=month&group_by=workspace[&resource=cpu]   (platform admins)
 *
 * Shape notes:
 * - `series[].points[i]` lines up with `buckets[i]` and `days[i]` (Europe/London days for step=1d).
 * - Future buckets have `forecast: true` and null cost and value. The month-end forecast comes as one number,
 *   `forecast_total`; the dashboard spreads it over the remaining days by each series' recent run rate.
 * - `value` and `average` are only set when the series is a single resource: always with group_by=resource, or with
 *   any grouping when `resource=` names one resource.
 * - With group_by=resource the series follow the fixed resource order; otherwise they are sorted by cost.
 */
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "../client";
import { qk } from "../keys";
import type { Schemas } from "../types";

export type UsageTimeseries = Schemas["UsageTimeseries"];
export type UsageSeries = Schemas["UsageTimeseriesSeries"];
export type UsagePoint = Schemas["UsageTimeseriesPoint"];
export type UsageResourceTotal = Schemas["UsageResourceTotal"];
export type UsageResource = Schemas["UsageResource"];
export type UsageRange = NonNullable<UsageTimeseries["range"]>;
export type UsageGroupBy = UsageTimeseries["group_by"];

export interface UsageTimeseriesFilter {
  range: UsageRange;
  group_by: UsageGroupBy;
  /** Comma-separated resources to include. */
  resource?: string;
  /** Project scope only. */
  environment?: string;
}

export type UsageScope = { type: "project"; project: string } | { type: "workspace"; workspace: string } | { type: "platform" };

function toQuery(f: UsageTimeseriesFilter) {
  return {
    range: f.range,
    // Only the platform scope groups by workspace; usageTimeseriesQuery never sends it to these endpoints.
    group_by: f.group_by as Exclude<UsageGroupBy, "workspace">,
    step: "1d" as const,
    ...(f.resource ? { resource: f.resource } : {}),
  };
}

export function usageTimeseriesQuery(scope: UsageScope, filter: UsageTimeseriesFilter) {
  if (scope.type === "platform") {
    return {
      queryKey: qk.admin.usageTimeseries(filter) as readonly unknown[],
      queryFn: (): Promise<UsageTimeseries> =>
        unwrap(api.GET("/v1/admin/usage/timeseries", { params: { query: { range: filter.range, group_by: filter.group_by as "resource" | "workspace", ...(filter.resource ? { resource: filter.resource } : {}) } } })),
      enabled: true,
      placeholderData: <T,>(prev: T) => prev,
      staleTime: 60_000,
    };
  }
  const slug = scope.type === "project" ? scope.project : scope.workspace;
  return {
    queryKey: (scope.type === "project" ? qk.usageTimeseries(slug, filter) : qk.workspaceUsageTimeseries(slug, filter)) as readonly unknown[],
    queryFn: (): Promise<UsageTimeseries> =>
      scope.type === "project"
        ? unwrap(
            api.GET("/v1/projects/{project}/usage/timeseries", {
              params: { path: { project: slug }, query: { ...toQuery(filter), ...(filter.environment ? { environment: filter.environment } : {}) } },
            }),
          )
        : unwrap(api.GET("/v1/workspaces/{workspace}/usage/timeseries", { params: { path: { workspace: slug }, query: toQuery(filter) } })),
    enabled: !!slug,
    placeholderData: <T,>(prev: T) => prev,
    staleTime: 60_000,
  };
}

export function useUsageTimeseries(scope: UsageScope, filter: UsageTimeseriesFilter, opts: { enabled?: boolean } = {}) {
  const q = usageTimeseriesQuery(scope, filter);
  return useQuery({ ...q, enabled: q.enabled && (opts.enabled ?? true) });
}
